import type { PaymentListItem, TransactionListItem } from '@pet/shared';
import { shortPropertyLabel } from '../pages/spending/controls';
import { utcMonthKey, utcYear, formatUtcMonth } from './utcDate';

// Filter and sort for View Payments and View Expenses, run in the browser on the loaded
// list (read views V1). Pure: no React, no fetch. Every function returns a new array.
// Chip values come from the loaded rows only, so a filter can never offer a value with no
// match. No dollar figure is computed here (AD-17): only which rows show, and in what order.

export type SortKey = 'newest' | 'oldest' | 'largest' | 'smallest' | 'payeeAsc' | 'payeeDesc';

export const DEFAULT_SORT: SortKey = 'newest';

export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'largest', label: 'Largest amount first' },
  { value: 'smallest', label: 'Smallest amount first' },
  { value: 'payeeAsc', label: 'Paid to A→Z' },
  { value: 'payeeDesc', label: 'Paid to Z→A' },
];

// The two sorts under which rows are grouped under month headings.
export function isDateSort(sort: SortKey): boolean {
  return sort === 'newest' || sort === 'oldest';
}

// What a sort reads from a row: payments sort on totalCents, expenses on amountCents.
interface SortView {
  id: string;
  date: string;
  amountCents: number;
  payee: string;
}

// Code-unit order: what SQLite's BINARY collation gives the server's `id: 'asc'`.
function compareCodeUnits(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function comparePayees(a: string, b: string): number {
  return a.localeCompare(b, 'en', { sensitivity: 'base' });
}

// Ties always fall back to date descending, then id ascending — the server's own order
// (`orderBy: [{ date: 'desc' }, { id: 'asc' }]`), so Newest first reproduces it exactly.
function compareTie(a: SortView, b: SortView): number {
  return Date.parse(b.date) - Date.parse(a.date) || compareCodeUnits(a.id, b.id);
}

export function compareForSort(sort: SortKey, a: SortView, b: SortView): number {
  let primary = 0;
  switch (sort) {
    case 'newest':
      break;
    case 'oldest':
      primary = Date.parse(a.date) - Date.parse(b.date);
      break;
    case 'largest':
      primary = b.amountCents - a.amountCents;
      break;
    case 'smallest':
      primary = a.amountCents - b.amountCents;
      break;
    case 'payeeAsc':
      primary = comparePayees(a.payee, b.payee);
      break;
    case 'payeeDesc':
      primary = comparePayees(b.payee, a.payee);
      break;
  }
  return primary || compareTie(a, b);
}

function paymentView(p: PaymentListItem): SortView {
  return { id: p.id, date: p.date, amountCents: p.totalCents, payee: p.payee };
}

function expenseView(t: TransactionListItem): SortView {
  return { id: t.id, date: t.date, amountCents: t.amountCents, payee: t.vendorOrPayer };
}

export function sortPayments(rows: PaymentListItem[], sort: SortKey): PaymentListItem[] {
  return [...rows].sort((a, b) => compareForSort(sort, paymentView(a), paymentView(b)));
}

export function sortExpenses(rows: TransactionListItem[], sort: SortKey): TransactionListItem[] {
  return [...rows].sort((a, b) => compareForSort(sort, expenseView(a), expenseView(b)));
}

// --- Filters: one choice per group; null is "All". ---

// "Who paid": the LLC, or one owner by id (the label is the owner's name).
export const WHO_PAID_LLC = 'llc';
const OWNER_PREFIX = 'owner:';

export interface PaymentFilters {
  house: string | null; // a property id
  whoPaid: string | null; // WHO_PAID_LLC or 'owner:<ownerId>'
  year: string | null; // e.g. "2026", UTC
  paidTo: string | null; // an exact payee string
}

export interface ExpenseFilters {
  house: string | null; // a property id
  category: string | null; // a taxCategoryKey
  year: string | null;
  paidTo: string | null; // an exact vendorOrPayer string
}

export const DEFAULT_PAYMENT_FILTERS: PaymentFilters = { house: null, whoPaid: null, year: null, paidTo: null };
export const DEFAULT_EXPENSE_FILTERS: ExpenseFilters = { house: null, category: null, year: null, paidTo: null };

function yearOf(iso: string): string {
  return String(utcYear(iso));
}

function matchesWhoPaid(p: PaymentListItem, whoPaid: string): boolean {
  if (whoPaid === WHO_PAID_LLC) return p.paymentSource === 'LLC_FUNDED';
  return (
    p.paymentSource === 'OWNER_FUNDED' &&
    p.ownerId !== null &&
    whoPaid === `${OWNER_PREFIX}${p.ownerId}`
  );
}

export function filterPayments(rows: PaymentListItem[], f: PaymentFilters): PaymentListItem[] {
  return rows.filter(
    (p) =>
      // A split payment is on every one of its houses.
      (f.house === null || p.properties.some((h) => h.id === f.house)) &&
      (f.whoPaid === null || matchesWhoPaid(p, f.whoPaid)) &&
      (f.year === null || yearOf(p.date) === f.year) &&
      (f.paidTo === null || p.payee === f.paidTo)
  );
}

export function filterExpenses(rows: TransactionListItem[], f: ExpenseFilters): TransactionListItem[] {
  return rows.filter(
    (t) =>
      (f.house === null || t.propertyId === f.house) &&
      (f.category === null || t.taxCategoryKey === f.category) &&
      (f.year === null || yearOf(t.date) === f.year) &&
      (f.paidTo === null || t.vendorOrPayer === f.paidTo)
  );
}

// Any filter set (the sort is not a filter).
export function hasActiveFilter(f: PaymentFilters | ExpenseFilters): boolean {
  return Object.values(f).some((v) => v !== null);
}

// A house chosen and nothing else filtered: the one case View Expenses shows a total for.
export function isHouseOnly(f: ExpenseFilters): boolean {
  return f.house !== null && f.category === null && f.year === null && f.paidTo === null;
}

// --- Chip values, from the loaded rows ---

export interface Choice {
  value: string;
  label: string;
}

function byAddressThenId(a: { id: string; address: string }, b: { id: string; address: string }): number {
  return compareCodeUnits(a.address, b.address) || compareCodeUnits(a.id, b.id);
}

function houseChoices(houses: { id: string; address: string }[]): Choice[] {
  const byId = new Map<string, { id: string; address: string }>();
  for (const h of houses) if (!byId.has(h.id)) byId.set(h.id, h);
  return [...byId.values()]
    .sort(byAddressThenId)
    .map((h) => ({ value: h.id, label: shortPropertyLabel(h.address) }));
}

// Exact strings: two spellings of one vendor are two chips. Case-insensitive A–Z, with an
// exact-string tiebreak so case variants keep a stable order.
function payeeChoices(payees: string[]): Choice[] {
  return [...new Set(payees)]
    .sort((a, b) => comparePayees(a, b) || compareCodeUnits(a, b))
    .map((p) => ({ value: p, label: p }));
}

function yearChoices(dates: string[]): Choice[] {
  return [...new Set(dates.map(yearOf))]
    .sort((a, b) => Number(b) - Number(a))
    .map((y) => ({ value: y, label: y }));
}

export interface PaymentChoices {
  houses: Choice[];
  whoPaid: Choice[];
  years: Choice[];
  payees: Choice[];
}

export function paymentChoices(rows: PaymentListItem[]): PaymentChoices {
  const owners = new Map<string, string>();
  let anyLlc = false;
  for (const p of rows) {
    if (p.paymentSource === 'LLC_FUNDED') anyLlc = true;
    else if (p.ownerId !== null && !owners.has(p.ownerId)) owners.set(p.ownerId, p.ownerName ?? 'An owner');
  }
  const ownerChoices = [...owners.entries()]
    .sort(([idA, a], [idB, b]) => comparePayees(a, b) || compareCodeUnits(idA, idB))
    .map(([id, name]) => ({ value: `${OWNER_PREFIX}${id}`, label: name }));
  return {
    houses: houseChoices(rows.flatMap((p) => p.properties)),
    whoPaid: [...(anyLlc ? [{ value: WHO_PAID_LLC, label: 'The LLC' }] : []), ...ownerChoices],
    years: yearChoices(rows.map((p) => p.date)),
    payees: payeeChoices(rows.map((p) => p.payee)),
  };
}

export interface ExpenseChoices {
  houses: Choice[];
  categories: Choice[];
  years: Choice[];
  payees: Choice[];
}

export function expenseChoices(rows: TransactionListItem[]): ExpenseChoices {
  const categories = new Map<string, string>();
  for (const t of rows) if (!categories.has(t.taxCategoryKey)) categories.set(t.taxCategoryKey, t.taxCategoryLabel);
  return {
    houses: houseChoices(rows.map((t) => ({ id: t.propertyId, address: t.propertyAddress }))),
    categories: [...categories.entries()]
      .sort(([keyA, a], [keyB, b]) => comparePayees(a, b) || compareCodeUnits(keyA, keyB))
      .map(([key, label]) => ({ value: key, label })),
    years: yearChoices(rows.map((t) => t.date)),
    payees: payeeChoices(rows.map((t) => t.vendorOrPayer)),
  };
}

// --- Chip wiring: "All" first, and no value can collide with it ---

export const ALL_CHIP = 'all';

export function chipOptions(choices: Choice[]): Choice[] {
  return [{ value: ALL_CHIP, label: 'All' }, ...choices.map((c) => ({ value: `v:${c.value}`, label: c.label }))];
}

export function toChip(value: string | null): string {
  return value === null ? ALL_CHIP : `v:${value}`;
}

export function fromChip(chip: string): string | null {
  return chip === ALL_CHIP ? null : chip.slice(2);
}

// --- The active-choices line ---

function labelOf(choices: Choice[], value: string | null): string | null {
  if (value === null) return null;
  return choices.find((c) => c.value === value)?.label ?? null;
}

function sortPart(sort: SortKey): string[] {
  return sort === DEFAULT_SORT ? [] : [SORT_OPTIONS.find((o) => o.value === sort)!.label];
}

// The words for each choice that differs from the defaults, in the panel's order.
export function paymentActiveParts(f: PaymentFilters, sort: SortKey, choices: PaymentChoices): string[] {
  const whoPaid = labelOf(choices.whoPaid, f.whoPaid);
  const parts = [
    ...sortPart(sort),
    labelOf(choices.houses, f.house),
    whoPaid === null ? null : f.whoPaid === WHO_PAID_LLC ? 'Paid by the LLC' : `Paid by ${whoPaid}`,
    f.year,
    f.paidTo === null ? null : `Paid to ${f.paidTo}`,
  ];
  return parts.filter((p): p is string => p !== null);
}

export function expenseActiveParts(f: ExpenseFilters, sort: SortKey, choices: ExpenseChoices): string[] {
  const parts = [
    ...sortPart(sort),
    labelOf(choices.houses, f.house),
    labelOf(choices.categories, f.category),
    f.year,
    f.paidTo === null ? null : `Paid to ${f.paidTo}`,
  ];
  return parts.filter((p): p is string => p !== null);
}

// --- Month headings ---

export interface MonthGroup<T> {
  key: string;
  label: string; // "September 2026", UTC
  rows: T[];
}

// Consecutive runs of rows in one UTC month, in the order given — under a date sort, each
// month appears once.
export function monthGroups<T extends { date: string }>(rows: T[]): MonthGroup<T>[] {
  const groups: MonthGroup<T>[] = [];
  for (const row of rows) {
    const key = utcMonthKey(row.date);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.rows.push(row);
    else groups.push({ key, label: formatUtcMonth(row.date), rows: [row] });
  }
  return groups;
}
